import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Button, message, Typography, Grid, Space, Card, Layout } from 'antd';
import { UserAddOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { getUsers, createUser, updateUser, deleteUser, getUserToken, getProducts } from './UsersServices';
import UserTable from './UserTable/UserTable';
import UserModal from './UserModal/UserModal';
import UserViewModal from './UserViewModal/UserViewModal';
import { format } from 'date-fns';
import { Input } from 'antd';
import { debounce } from 'lodash';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { saveAuthDataForAdmin } from '../../Config/ApiConfig';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);

const { Title } = Typography;
const { useBreakpoint } = Grid;

const UserList = () => {
    const { changeUser } = useGlobal();
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const { handleError } = useErrorHandler();
    const [modalVisible, setModalVisible] = useState(false);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [viewingUser, setViewingUser] = useState(null);
    const [searchString, setSearchString] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sort_by, setSortBy] = useState(undefined);
    const [sort_direction, setSortDirection] = useState(undefined);
    const [filters, setFilters] = useState(undefined);
    const [pagination, setPagination] = useState({
        total: 0,
        last: false,
    });

    const screens = useBreakpoint();

    const fetchUsers = useCallback(async (search_string) => {
        try {
            setLoading(true);
            const response = await getUsers({ page, size: pageSize, sort_by, sort_direction, search_string, filters });
            setUsers(
                response.data.map(user => ({ 
                    ...user, 
                    discount_expiry: user.discount_expiry ? dayjs(user.discount_expiry) : null 
                }))
            );
            setPagination({
                total: response.metadata.total,
                last: response.metadata.last,
            });
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, page, pageSize, sort_by, sort_direction, filters]);

    const getUserTokenCall = useCallback(async (id) => {
        setLoading(true);
        try {
            const { data } = await getUserToken(id);
            saveAuthDataForAdmin(data);
            changeUser(data.user);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, changeUser]);

    const handleSearch = (e) => {
        setSearchString(e.target.value);
        setPage(1);
        debouncedFetchUsers(e.target.value);
    };

    const debouncedFetchUsers = useCallback(
        debounce((search) => {
            fetchUsers(search);
        }, 500),
        [fetchUsers]
    );

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleTableChange = (pagination, filters, sorter) => {
        const { order, field } = sorter || {};
        let sort_by = field;
        if (field === 'role') {
            sort_by = 'is_admin';
        }
        const sort_direction = order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : undefined;
        if (pagination.current) {
            setPage(pagination.current);
        }
        if (pagination.pageSize) {
            setPageSize(pagination.pageSize);
        }
        
        // Reset sort state when order is undefined (sorting is removed)
        if (order === undefined) {
            setSortBy(undefined);
            setSortDirection(undefined);
        } else if (sort_by) {
            setSortBy(sort_by);
            
            if (sort_direction) {
                setSortDirection(sort_direction);
            }
        }
        
        if (filters) {
            const statusFilters = {};
            if (filters.status) {
                filters.status.forEach(status => {
                    if (status === 'active') statusFilters.is_disabled = false;
                    if (status === 'inactive') statusFilters.is_disabled = true;
                    if (status === 'verified') statusFilters.is_verified = true;
                    if (status === 'unverified') statusFilters.is_verified = false;
                });
            }
            setFilters(statusFilters);
            setPage(1);
        }
    };

    const showModal = (user = null) => {
        setEditingUser(user);
        setModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        setEditingUser(null);
    };

    const showViewModal = (user = null) => {
        setViewingUser(user);
        setViewModalVisible(true);
    };

    const handleViewCancel = () => {
        setViewModalVisible(false);
        setViewingUser(null);
    };

    const handleFormSubmit = async (values) => {
        try {
            const defaultValues = {
                ...values,
                discount_percentage: values.discount_percentage
                    ? parseFloat(values.discount_percentage)
                    : undefined,
                discount_expiry: values.discount_expiry
                    ? dayjs(values.discount_expiry).utc().format('YYYY-MM-DDTHH:mm:ss+00:00')
                    : undefined,
            }
            let updatedValues = {
                ...defaultValues,
            };
            if (defaultValues.timezone) {
                const timezone = defaultValues.timezone;
                const offset = moment.tz(timezone).utcOffset() / 60;
                updatedValues = {
                    ...defaultValues,
                    timezone: offset,
                };
            }
            const filteredValues = Object.fromEntries(
                Object.entries(updatedValues).filter(([_, v]) => v != null)
            );
            if (editingUser) {
                await updateUser(editingUser.id, filteredValues);
                message.success(t('user_updated_successfully'));
            } else {
                await createUser(filteredValues);
                message.success(t('user_created_successfully'));
            }
            handleCancel();
            fetchUsers(searchString);
        } catch (error) {
            handleError(error);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            await deleteUser(id);
            message.success(t('user_deleted_successfully'));
            fetchUsers(searchString);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };


    return (
        <Layout>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }} size='medium'
                    title={t('user_management')}
                    extra={
                        <Button
                            type="link"
                            icon={<PlusOutlined />}
                            size="medium"
                            onClick={() => showModal()}
                        >
                            {t('add_user')}
                        </Button>
                    }
                    styles={{
                        body: {
                            padding: 16
                        }
                    }}>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder={t('search_users')}
                        value={searchString}
                        onChange={handleSearch}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        style={{ 
                            width: isInputFocused ? '100%' : (screens.xs ? '100%' : '300px'),
                            transition: 'width 0.4s ease-in-out'
                        }}
                    />
                </Card>
                <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                    <UserTable
                        users={users}
                        loading={loading}
                        pagination={pagination}
                        screens={screens}
                        handleTableChange={handleTableChange}
                        showModal={showModal}
                        handleDelete={handleDelete}
                        showViewModal={showViewModal}
                        getUserTokenCall={getUserTokenCall}
                        setLoading={setLoading}
                        searchString={searchString}
                    />
                </Card>
                <UserModal
                    modalVisible={modalVisible}
                    handleCancel={handleCancel}
                    handleSubmit={handleFormSubmit}
                    editingUser={editingUser}
                    screens={screens}
                    setLoading={setLoading}
                />
                <UserViewModal
                    modalVisible={viewModalVisible}
                    handleCancel={handleViewCancel}
                    user={viewingUser}
                    screens={screens}
                />
            </Space>
        </Layout>
    );
};

export default UserList;
